import type { Asset, AssetGroup, AssetType, Scene } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

/**
 * A serializable snapshot of an asset (and, for AssetGroups, its nested
 * children) that can be stored — e.g. in the clipboard — and later rebuilt as a
 * sticker via {@link buildStickerFromSerializedData}.
 */
export type SerializedStickerAsset = {
    assetType: AssetType;
    data: Record<string, unknown>;
    children?: SerializedStickerAsset[];
};

function getAssetTypeForCopy(asset: Asset) {
    if (VideoData.isAssetGroup(asset)) {
        return VideoData.GROUP_ASSET_TYPE;
    }
    if (VideoData.isTextAsset(asset)) {
        return VideoData.TEXT_ASSET_TYPE;
    }
    if (VideoData.isFancyCaptionAsset(asset)) {
        return VideoData.FANCY_CAPTION_ASSET_TYPE;
    }
    if (VideoData.isMediaAsset(asset)) {
        return VideoData.MEDIA_ASSET_TYPE;
    }
    if (VideoData.isIconAsset(asset)) {
        return VideoData.ICON_ASSET_TYPE;
    }
    if (VideoData.isChartAsset(asset)) {
        return VideoData.CHART_ASSET_TYPE;
    }
    if (VideoData.isColorAsset(asset)) {
        return VideoData.COLOR_ASSET_TYPE;
    }
    if (VideoData.isRectangleAsset(asset)) {
        return VideoData.RECTANGLE_ASSET_TYPE;
    }
    if (VideoData.isCircleAsset(asset)) {
        return VideoData.CIRCLE_ASSET_TYPE;
    }
    if (VideoData.isGradientAsset(asset)) {
        return VideoData.GRADIENT_ASSET_TYPE;
    }
    throw new Error(`copyAssetToContainer: unsupported asset type for ${asset.name}`);
}

function addStickerToTargetContainer(targetContainer: Scene | AssetGroup, assetType: AssetType) {
    return targetContainer.addSticker(assetType);
}

/**
 * Recursively serialize an asset into a plain, JSON-safe snapshot. AssetGroups
 * are captured together with their nested children so the whole tree can be
 * reconstructed later.
 */
export function serializeAssetAsSticker(sourceAsset: Asset): SerializedStickerAsset {
    if (VideoData.isAssetGroup(sourceAsset)) {
        return {
            assetType: VideoData.GROUP_ASSET_TYPE,
            data: sourceAsset.save(),
            children: sourceAsset
                .getAssets({ deep: false })
                .map((child) => serializeAssetAsSticker(child)),
        };
    }

    return {
        assetType: getAssetTypeForCopy(sourceAsset),
        data: sourceAsset.save(),
    };
}

/**
 * Rebuild an asset from a {@link SerializedStickerAsset} snapshot, adding it to
 * the target container as a sticker with freshly generated ids. AssetGroups are
 * rebuilt by recreating their children first, then loading the group-level data.
 */
export function buildStickerFromSerializedData(
    serialized: SerializedStickerAsset,
    targetContainer: Scene | AssetGroup,
): Asset {
    if (serialized.assetType === VideoData.GROUP_ASSET_TYPE) {
        const newGroup = addStickerToTargetContainer(
            targetContainer,
            VideoData.GROUP_ASSET_TYPE,
        ) as AssetGroup;
        (serialized.children ?? []).forEach((child) => {
            buildStickerFromSerializedData(child, newGroup);
        });
        newGroup.loadAsSticker(serialized.data);

        newGroup.generateNewId();
        return newGroup;
    }

    const newAsset = addStickerToTargetContainer(targetContainer, serialized.assetType);
    newAsset.loadAsSticker(serialized.data);
    newAsset.generateNewId();
    return newAsset;
}

export function copyAssetToContainerAsSticker(
    sourceAsset: Asset,
    targetContainer: Scene | AssetGroup,
): Asset {
    return buildStickerFromSerializedData(serializeAssetAsSticker(sourceAsset), targetContainer);
}
